import Vue from "vue";
import axios from "axios";
import {reactive, toRefs, computed, provide, inject} from "@vue/composition-api";
import {mergeGroups} from "../store_helpers";
import {$t} from "@/i18n";

export const state = reactive({
    report: {
        total: null,
        extra: null,
        time_entries: [],
        childs: [],
        tasks: [],
        task_count: null,
        time_entries_count: null,
        page: null,
        page_size: null,
        rows: [],
        row_count: null,
        period_coefficient: null,
        period_from: null,
        period_until: null,
        date_from: null,
        date_until: null,
        data_changed: false
    },
    filters: {
        user_accounts: [],
        clients: [],
        client_groups: [],
        topics: [],
        companies: [],
        task_custom_fields: [],
        projects: [],
        is_flextime_active: false
    }
});

export const actions = {
    fetchReport(params) {
        return new Promise(function (resolve, reject) {
            state.report.data_changed = false;
            axios
                .get(Vue.config.url_prefix + "/api/report", {params: params})
                .then(function (resp) {
                    state.report = Object.assign({}, state.report, resp.data);
                    resolve();
                })
                .catch(ex => reject(ex));
        });
    },
    fetchReportFilters() {
        return new Promise(function (resolve, reject) {
            axios
                .get(Vue.config.url_prefix + "/api/report_input")
                .then(function (resp) {
                    let data = resp.data;
                    data.user_accounts = mergeGroups(data.user_accounts)

                    state.filters = Object.assign({}, state.filters, data);
                    resolve();
                })
                .catch(ex => reject(ex));
        });
    },
    clearReports() {
        state.report.time_entries = [];
        state.report.time_entries_count = null;
        state.report.tasks = [];
        state.report.task_count = null;
        state.report.rows = [];
        state.report.row_count = null;
        state.report.childs = [];
        state.report.data_changed = false;
    },
    onWsTaskChange(t) {
        for (let i = 0; i < state.report.tasks.length; i++) {
            if (state.report.tasks[i].id && t.id && state.report.tasks[i].id === t.id) {
                state.report.data_changed = true;
                state.report.tasks[i].dirty = 1;
                break;
            }
            if (state.report.tasks[i].virtual_id && t.virtual_id && state.report.tasks[i].virtual_id === t.virtual_id) {
                state.report.data_changed = true;
                state.report.tasks[i].dirty = 1;
                break;
            }
        }
        for (let i = 0; i < state.report.time_entries.length; i++) {
            if (state.report.time_entries[i].task_id === t.id) {
                state.report.data_changed = true;
                state.report.time_entries[i].dirty = 1;
            }
        }
    },
    onWsTaskDelete(fx_equals) {
        for (let i = state.report.tasks.length - 1; i >= 0; i--) {
            if (fx_equals(state.report.tasks[i])) {
                state.report.data_changed = true;
                state.report.tasks[i].dirty = 1;
            }
        }
        for (let i = state.report.time_entries.length - 1; i >= 0; i--) {
            if (fx_equals(state.report.time_entries[i])) {
                state.report.data_changed = true;
                if (state.report.tasks[i]) {
                    state.report.tasks[i].dirty = 1;
                }
            }
        }
    }
};

export const getters = {
    current_page: computed(() => {
        return state.report.page;
    }),
    show_tasks_load_more: computed(() => {
        return state.report.task_count > state.report.tasks.length;
    }),
    show_entries_load_more: computed(() => {
        return state.report.time_entries_count > state.report.time_entries.length;
    }),
    tasks: computed(() => state.report.tasks),
    //total_hours: computed(() => format_time_from_seconds(state.report.total)),
    //extra_work: computed(() => format_time_from_seconds(state.report.extra)),
    users: computed(() => state.filters.user_accounts),
    users_map: computed(() =>
        state.filters.user_accounts.reduce((mapAccumulator, user) => {
            mapAccumulator[user.id] = user;
            return mapAccumulator;
        }, {})
    ),
    isFlextimeActive: computed(() => state.filters.is_flextime_active),
    getAssigneesByIds: ids => {
        return ids.map(id => {
            if (getters.users_map.value[id]) {
                return {
                    assigned_initials: getters.users_map.value[id].initials,
                    assigned_avatar: getters.users_map.value[id].avatar
                };
            } else {
                return {
                    assigned_initials: ""
                };
            }
        });
    },
    clients: computed(() => {
        return state.filters.clients;
    }),
    clients_map: computed(() =>
        state.filters.clients.reduce((mapAccumulator, client) => {
            mapAccumulator[client.id] = client;
            return mapAccumulator;
        }, {})
    ),
    client_groups: computed(() => state.filters.client_groups),
    topics: computed(() => [{id: 0, name: $t("(Without topic)"), billable: 1}, ...state.filters.topics.filter(t => !!t.id)]),
    topics_map: computed(() =>
        getters.topics.value.reduce((mapAccumulator, topic) => {
            mapAccumulator[topic.id] = topic;
            return mapAccumulator;
        }, {})
    ),
    projects: computed(() => [{id: 0, name: $t("(Without project)")}, ...state.filters.projects.filter(p => !!p.id)]),
    projects_map: computed(() =>
        getters.projects.value.reduce((mapAccumulator, project) => {
            mapAccumulator[project.id] = project;
            return mapAccumulator;
        }, {})
    ),
    summary_childs: computed(() => (state.report && state.report.childs ? state.report.childs : [])),
    summary_childs_max: computed(() => {
        if (state.report && state.report.childs) {
            return Math.max(...state.report.childs.map(c => c.total + c.extra), 0);
        } else {
            return 0;
        }
    }),
    merged_topics: computed(() => {
        // fixme :: topic temporary (show all)
        // temporary solution that shows all topics in filters
        return getters.topics.value;
        /* todo :: a solution that shows one topic name that has grouped topic ids as values
        const merge_map = new Map();
        getters.topics.value.forEach(topic => {
            const previous = merge_map.get(topic.name);
            const topic_id_list = [];
            if (previous) {
                topic_id_list.push(...previous.ids);
            }
            topic_id_list.push(topic.id);
            merge_map.set(topic.name, {name: topic.name, ids: topic_id_list, ids_str: topic_id_list.join(",")});
        });
        return Array.from(merge_map.values());
        */
    }),
    companies: computed(() => state.filters.companies),
    custom_fields: computed(() => state.filters.task_custom_fields.filter(item => !!item.ids)),

    summary_custom_fields: computed(() =>
        state.filters.task_custom_fields.filter(item => ["custom", "aggregation"].includes(item.group))
    ),

    details_custom_fields: computed(() =>
        state.filters.task_custom_fields.filter(item => ["standard", "custom", "task", "task_assignees"].includes(item.group))
    ),

    time_custom_fields: computed(() =>
        state.filters.task_custom_fields.filter(item => ["standard", "custom", "time_entry", "task"].includes(item.group))
    ),

    flextime_custom_fields: computed(() =>
        state.filters.task_custom_fields.filter(item => ["flextime"].includes(item.group))
    ),

    monitoring_fields: computed(() =>
        state.filters.task_custom_fields.filter(item => ["monitoring"].includes(item.group))
    ),

    time_entries: computed(() =>
        state.report.time_entries.map(entry => {
            let topic = getters.topics_map.value[entry.topic_id]
                ? getters.topics_map.value[entry.topic_id]
                : {name: ""};
            let user = getters.users_map.value[entry.person_id];
            return {
                ...entry,
                topic,
                user
            };
        })
    ),
    monitoring_rows: computed(() => state.report.rows),
    period_coefficient: computed(() => state.report.period_coefficient),
    period_from: computed(() => state.report.period_from),
    period_until: computed(() => state.report.period_until),
    date_from: computed(() => state.report.date_from),
    date_until: computed(() => state.report.date_until),
    calcExtraColumnValue: (child, column) => {
        if (["extra_h", "total_h"].includes(column)) {
            return (child[column] / (60 * 60)).toFixed(4);
        }
        return child[column];
    },
    data_changed: computed(() => state.report.data_changed)
};

export const key = Symbol("ReportModule");

export const provideReport = () => {
    provide(key, {
        ...toRefs(state),
        ...toRefs(getters),
        ...actions
    });
};

export default () => inject(key);
